import React, { useEffect, useRef, useState } from 'react';
import { Application, FederatedPointerEvent, Graphics } from 'pixi.js';
import type { ClickPoint, PathSegment } from '../worker-messages';
import { TOKENS } from '../tokens';
import StageLayers, { StageLayerSet } from '../StageLayers';

const LOGICAL_WIDTH = TOKENS.stage.w;
const LOGICAL_HEIGHT = TOKENS.stage.h;
const TEXT_COLOR = Number(TOKENS.text.replace('#', '0x'));

interface Stage9x16Props {
  /** Called once when the Pixi application is ready. */
  onApp?: (app: Application) => void;
  /** Called with layered containers for adding display objects. */
  onLayers?: (layers: StageLayerSet) => void;
  /** Pointer down in logical coordinates. */
  onPointerDown?: (x: number, y: number, e: FederatedPointerEvent) => void;
  /** Pointer move in logical coordinates. */
  onPointerMove?: (x: number, y: number, e: FederatedPointerEvent) => void;
  /** Pointer up in logical coordinates. */
  onPointerUp?: (x: number, y: number, e: FederatedPointerEvent) => void;
  /** Raw beat positions used for faint overlays. */
  beats?: ClickPoint[];
  /** Click points for each difficulty. */
  points?: ClickPoint[][];
  /** Smoothed path segments for each difficulty. */
  paths?: PathSegment[][];
  /** Toggle rendering of beat circles. */
  showBeats?: boolean;
  /** Toggle rendering of click points. */
  showPoints?: boolean;
  /** Toggle rendering of paths. */
  showPaths?: boolean;
  /** Optional highlighted point for scrubbing. */
  scrubPoint?: ClickPoint | null;
}

/**
 * Creates a PixiJS stage with logical 9x16 dimensions (900×1600) and scales
 * it to fit the window while preserving aspect ratio with letterboxing.
 * Pointer and touch events are mapped to this logical space and exposed via
 * callback props.
 */
export default function Stage9x16({
  onApp,
  onLayers,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  beats = [],
  points = [],
  paths = [],
  showBeats = true,
  showPoints = true,
  showPaths = true,
  scrubPoint = null,
}: Stage9x16Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pointerDownRef = useRef(onPointerDown);
  const pointerMoveRef = useRef(onPointerMove);
  const pointerUpRef = useRef(onPointerUp);
  const [app, setApp] = useState<Application | null>(null);
  const beatsRef = useRef<Graphics>();
  const pointsRef = useRef<Graphics>();
  const pathsRef = useRef<Graphics>();
  const scrubRef = useRef<Graphics>();

  useEffect(() => {
    pointerDownRef.current = onPointerDown;
  }, [onPointerDown]);
  useEffect(() => {
    pointerMoveRef.current = onPointerMove;
  }, [onPointerMove]);
  useEffect(() => {
    pointerUpRef.current = onPointerUp;
  }, [onPointerUp]);

  useEffect(() => {
    let appInstance: Application | null = null;
    let layers: StageLayerSet | null = null;
    let resize = () => {};
    let handleDown: ((e: FederatedPointerEvent) => void) | null = null;
    let handleMove: ((e: FederatedPointerEvent) => void) | null = null;
    let handleUp: ((e: FederatedPointerEvent) => void) | null = null;
    let destroyed = false;

    const init = async () => {
      appInstance = new Application();
      await appInstance.init({
        width: LOGICAL_WIDTH,
        height: LOGICAL_HEIGHT,
        backgroundAlpha: 0,
      });
      if (destroyed) {
        // Component was unmounted before init completed
        appInstance.destroy(true);
        return;
      }

      const canvas = appInstance.canvas as HTMLCanvasElement;
      const container = containerRef.current;
      container?.appendChild(canvas);

      // Enable pointer events across the whole stage.
      appInstance.stage.eventMode = 'static';
      appInstance.stage.hitArea = appInstance.screen;

      const mapEvent = (e: FederatedPointerEvent) => {
        const pt = { x: 0, y: 0 };
        appInstance!.renderer.events.mapPositionToPoint(pt, e.clientX, e.clientY);
        return pt;
      };

      handleDown = (e) => {
        const { x, y } = mapEvent(e);
        pointerDownRef.current?.(x, y, e);
      };
      handleMove = (e) => {
        const { x, y } = mapEvent(e);
        pointerMoveRef.current?.(x, y, e);
      };
      handleUp = (e) => {
        const { x, y } = mapEvent(e);
        pointerUpRef.current?.(x, y, e);
      };

      appInstance.stage.on('pointerdown', handleDown);
      appInstance.stage.on('pointermove', handleMove);
      appInstance.stage.on('pointerup', handleUp);

      // Layered containers and overlay graphics
      layers = StageLayers(appInstance);
      onLayers?.(layers);
      const beatG = new Graphics();
      const pathG = new Graphics();
      const pointG = new Graphics();
      const scrubG = new Graphics();
      layers.hud.addChild(beatG);
      layers.paths.addChild(pathG);
      layers.notes.addChild(pointG);
      layers.hud.addChild(scrubG);
      beatsRef.current = beatG;
      pathsRef.current = pathG;
      pointsRef.current = pointG;
      scrubRef.current = scrubG;

      resize = () => {
        const scale = Math.min(
          window.innerWidth / LOGICAL_WIDTH,
          window.innerHeight / LOGICAL_HEIGHT,
        );
        const screenWidth = LOGICAL_WIDTH * scale;
        const screenHeight = LOGICAL_HEIGHT * scale;
        canvas.style.width = `${screenWidth}px`;
        canvas.style.height = `${screenHeight}px`;
        canvas.style.position = 'absolute';
        canvas.style.left = `${(window.innerWidth - screenWidth) / 2}px`;
        canvas.style.top = `${(window.innerHeight - screenHeight) / 2}px`;
        layers?.resize(appInstance!.screen.width, appInstance!.screen.height);
      };

      window.addEventListener('resize', resize);
      resize();

      setApp(appInstance);
      onApp?.(appInstance);
    };

    void init();

    return () => {
      destroyed = true;
      window.removeEventListener('resize', resize);
      if (appInstance && appInstance.renderer) {
        if (handleDown) appInstance.stage.off('pointerdown', handleDown);
        if (handleMove) appInstance.stage.off('pointermove', handleMove);
        if (handleUp) appInstance.stage.off('pointerup', handleUp);
        appInstance.destroy(true);
      }
    };
  }, [onApp, onLayers]);

  // Draw beat circles
  useEffect(() => {
    const g = beatsRef.current;
    if (!app || !g) return;
    g.clear();
    if (!showBeats) return;
    g.setFillStyle({ color: TEXT_COLOR, alpha: 0.2 });
    for (const b of beats) {
      g.circle(b.x * 100, b.y * 100, 3);
    }
    g.fill();
  }, [app, beats, showBeats]);

  // Draw click points
  useEffect(() => {
    const g = pointsRef.current;
    if (!app || !g) return;
    g.clear();
    if (!showPoints) return;
    const colors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00];
    points.forEach((list, idx) => {
      g.setFillStyle({ color: colors[idx % colors.length], alpha: 1 });
      for (const p of list) {
        g.circle(p.x * 100, p.y * 100, 5);
      }
      g.fill();
    });
  }, [app, points, showPoints]);

  // Draw smoothed paths
  useEffect(() => {
    const g = pathsRef.current;
    if (!app || !g) return;
    g.clear();
    if (!showPaths) return;
    const colors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00];
    paths.forEach((segList, idx) => {
      g.setStrokeStyle({ width: 2, color: colors[idx % colors.length], alpha: 1 });
      segList.forEach((seg) => {
        if (seg.points.length === 0) return;
        g.moveTo(seg.points[0].x * 100, seg.points[0].y * 100);
        for (let i = 1; i < seg.points.length; i++) {
          const p = seg.points[i];
          g.lineTo(p.x * 100, p.y * 100);
        }
      });
      g.stroke();
    });
  }, [app, paths, showPaths]);

  // Highlight scrubbed point
  useEffect(() => {
    const g = scrubRef.current;
    if (!app || !g) return;
    g.clear();
    if (!scrubPoint) return;
    g.circle(scrubPoint.x * 100, scrubPoint.y * 100, 6).fill({ color: TEXT_COLOR, alpha: 1 });
  }, [app, scrubPoint]);

  return <div ref={containerRef} className="w-full h-full" />;
}

